import { OrderFeature, compareOrderFeaturesByCategory } from '@aloft/toolkit-shared';

import { useEffect, useRef } from 'react';

import { PropertyGridSection } from '../../../components/PropertyGrid/columnSections/PropertyGridSection.tsx';
import { PropertyGridSectionProps } from '../../../components/PropertyGrid/types.ts';
import { handleUpdatePropertyData } from '../../../components/PropertyGrid/utils.ts';
import { setOrderFeatures } from '../../../redux/features/orderSlice.ts';
import {
  selectIncludeCostApproach,
  selectOrderFeatures,
  selectShowCraftsmanQualityOnGrid,
  selectShowFeatureAreasOnGrid,
} from '../../../redux/features/selectors/orderSelectors.ts';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks.ts';
import { AppDispatch } from '../../../redux/types.ts';
import { FEATURE_GRID_ORDER_FEATURES } from '../consts.ts';

type FeatureGridFeatureSectionProps = PropertyGridSectionProps & {
  feature: OrderFeature;
};

/**
 * Clears form state (and syncs redux) for a feature that's no longer part of `orderFeatures`,
 * across all columns. Needed regardless of how the feature was removed (per-row remove button,
 * or unchecking it in the Edit Feature menu), so stale data doesn't linger/resurface.
 */
const clearFeatureFormState = (
  feature: OrderFeature,
  { getValues, setValue }: Pick<PropertyGridSectionProps, 'getValues' | 'setValue'>,
  dispatch: AppDispatch,
) => {
  const columns = getValues('columns');
  columns.forEach((column, columnIndex) => {
    if (column.type !== 'property') return;
    let { propertyData } = column;
    const { propertyId } = column;

    if (propertyData?.editableData?.improvements) {
      if (feature === 'other') {
        const displayNameField =
          `columns.${columnIndex}.propertyData.editableData.improvements.otherAdjustmentsDisplayName` as const;
        const adjustmentField =
          `columns.${columnIndex}.propertyData.editableData.manualAdjustments.other` as const;
        setValue(displayNameField as any, undefined);
        setValue(adjustmentField as any, undefined);
      } else {
        const featureFieldName = `columns.${columnIndex}.propertyData.editableData.improvements.${feature}`;
        setValue(featureFieldName as any, undefined);
      }

      propertyData = getValues(`columns.${columnIndex}.propertyData`);
      handleUpdatePropertyData(propertyId, getValues(), dispatch, propertyData);
    }
  });
};

const FeatureGridFeatureSection = (props: FeatureGridFeatureSectionProps) => {
  const { feature, hasUnitRate } = props;
  const includeCostApproach = useAppSelector(selectIncludeCostApproach);
  const showCraftsmanQualityOnGrid = useAppSelector(selectShowCraftsmanQualityOnGrid);
  const showFeatureAreasOnGrid = useAppSelector(selectShowFeatureAreasOnGrid);
  const { label, propertyGridFields: baseFields } = FEATURE_GRID_ORDER_FEATURES[feature];

  let propertyGridFields = baseFields;
  if (feature === 'manualCost' && !includeCostApproach) {
    propertyGridFields = propertyGridFields.filter(
      (f) => f.fieldName !== 'editableData.improvements.manualCost',
    );
  }
  if (!showFeatureAreasOnGrid && feature !== 'adu') {
    propertyGridFields = propertyGridFields.filter((f) => !f.fieldName.endsWith('.area'));
  }
  if (!showCraftsmanQualityOnGrid) {
    propertyGridFields = propertyGridFields.filter((f) => !f.fieldName.endsWith('.quality'));
  }

  if (hasUnitRate) {
    propertyGridFields.forEach((field) => {
      if (
        [
          'editableData.improvements.garage.spaces',
          'editableData.improvements.detachedGarage.spaces',
          'editableData.improvements.carport.spaces',
        ].includes(field.fieldName)
      ) {
        // eslint-disable-next-line no-param-reassign
        field.required = true;
      }
    });
  }

  return (
    <PropertyGridSection {...props} title={label} hasSeparateArvValue fields={propertyGridFields} />
  );
};

export const FeatureGridFeatureSections = (props: PropertyGridSectionProps) => {
  const dispatch = useAppDispatch();
  const { setValue, getValues } = props;
  const orderFeatures = useAppSelector(selectOrderFeatures);
  const includeCostApproach = useAppSelector(selectIncludeCostApproach);
  const sortedOrderFeatures: OrderFeature[] = orderFeatures
    .filter((feature) => feature !== 'manualCost' && feature !== 'other')
    .sort(compareOrderFeaturesByCategory);

  if (includeCostApproach && orderFeatures.includes('manualCost')) {
    sortedOrderFeatures.push('manualCost');
  }

  // Clear form state for any feature removed from `orderFeatures` since the last render,
  // regardless of how it was removed (per-row remove button, or the Edit Feature menu's Save).
  const previousOrderFeaturesRef = useRef(orderFeatures);
  useEffect(() => {
    const previousOrderFeatures = previousOrderFeaturesRef.current;
    const removedFeatures = previousOrderFeatures.filter((f) => !orderFeatures.includes(f));
    removedFeatures.forEach((feature) =>
      clearFeatureFormState(feature, { getValues, setValue }, dispatch),
    );
    previousOrderFeaturesRef.current = orderFeatures;
  }, [orderFeatures]);

  return sortedOrderFeatures.map((feature) => (
    <FeatureGridFeatureSection
      key={feature}
      feature={feature}
      isRemovable
      onRemove={() => dispatch(setOrderFeatures(orderFeatures.filter((f) => f !== feature)))}
      {...props}
    />
  ));
};
